import React, { useState } from 'react';
import { File, Download, Eye, FileVideo } from '~/ui/assets/icons';
import moment from 'moment';
import { PdfViewer } from '~/ui/components';
import {
  FileItem,
  FileImage,
  FileInfo,
  FileName,
  FileDetail,
  FileAction,
  ActionsRow,
  BottomInfo,
} from './FilesList.styled';

export type FileType = 'image' | 'document' | 'video' | 'audio' | 'pdf' | string;

export interface FileProps {
  name: string;
  type: FileType;
  size?: number;
  url?: string;
  date?: string;
  onClick?: () => void;
  onDownload?: () => void;
}

function FileSingle({ name, url, size, date, type, onClick, onDownload }: FileProps) {
  const isVideo = type === 'video';
  const isImage = type === 'image';

  const getFileIcon = (type: FileProps['type']) => {
    switch (type) {
      case 'video':
        return <FileVideo width={20} height={20} />;
      default:
        return <File width={20} height={20} />;
    }
  };

  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY [às] HH:mm');

  const formatSize = (size: number) => {
    if (size > 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
    if (size > 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (size > 1024) {
      return `${(size / 1024).toFixed(2)} KB`;
    }
    return `${size} B`;
  };

  const isPdf = type === 'pdf';
  const [pdfError, setPdfError] = useState(false);

  const renderPdfPreview = () => {
    if (pdfError) {
      return (
        <object
          data={`${url}#toolbar=0&navpanes=0`}
          type="application/pdf"
          width="100%"
          height="100%"
          aria-label={name}
        />
      );
    }

    return (
      <PdfViewer url={url} showControls={false} scale={0.3} noPadding noScroll onError={() => setPdfError(true)} />
    );
  };

  const renderPreview = () => {
    if (isPdf) {
      return renderPdfPreview();
    }

    if (isImage) {
      return <img src={url} alt={name} />;
    }

    return getFileIcon(type);
  };

  return (
    <FileItem>
      <FileImage>{url && !isVideo ? renderPreview() : getFileIcon(type)}</FileImage>

      <BottomInfo>
        <ActionsRow>
          <FileAction onClick={onClick} disabled={isVideo}>
            <Eye width={20} height={20} />
          </FileAction>

          <FileAction onClick={onDownload}>
            <Download width={20} height={20} />
          </FileAction>
        </ActionsRow>

        <FileInfo>
          <FileName>
            <span>
              {name} <FileDetail>{formatSize(size)}</FileDetail>
            </span>
          </FileName>
          <FileDetail>{date ? formatDate(date) : '-'}</FileDetail>
        </FileInfo>
      </BottomInfo>
    </FileItem>
  );
}

export default FileSingle;
